import { baseApi } from '@/services/api';
import { Booking, Ticket, PaymentIntent } from './bookingsTypes';

export const bookingsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Bookings
    getBookings: builder.query<Booking[], { user_id?: string; event_id?: string }>({
      query: (params) => ({
        url: '/bookings',
        method: 'GET',
        params,
      }),
      providesTags: ['Bookings'],
    }),
    
    getBooking: builder.query<Booking, string>({
      query: (id) => ({
        url: `/bookings/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Bookings', id }],
    }),
    
    createBooking: builder.mutation<Booking, Partial<Booking>>({
      query: (booking) => ({
        url: '/bookings',
        method: 'POST',
        data: booking,
      }),
      invalidatesTags: ['Bookings', 'Tickets'],
    }),
    
    updateBooking: builder.mutation<Booking, { id: string; data: Partial<Booking> }>({
      query: ({ id, data }) => ({
        url: `/bookings/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Bookings', id }, 'Bookings'],
    }),
    
    cancelBooking: builder.mutation<void, string>({
      query: (id) => ({
        url: `/bookings/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['Bookings', 'Tickets'],
    }),
    
    checkInBooking: builder.mutation<void, string>({
      query: (id) => ({
        url: `/bookings/${id}/check-in`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Bookings', id }],
    }),
    
    // Tickets
    getEventTickets: builder.query<Ticket[], string>({
      query: (eventId) => ({
        url: `/events/${eventId}/tickets`,
        method: 'GET',
      }),
      providesTags: ['Tickets'],
    }),
    
    createTicket: builder.mutation<Ticket, Partial<Ticket>>({
      query: (ticket) => ({
        url: '/tickets',
        method: 'POST',
        data: ticket,
      }),
      invalidatesTags: ['Tickets'],
    }),
    
    updateTicket: builder.mutation<Ticket, { id: string; data: Partial<Ticket> }>({
      query: ({ id, data }) => ({
        url: `/tickets/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Tickets'],
    }),
    
    // Payment
    createPaymentIntent: builder.mutation<PaymentIntent, { event_id: string; amount: number; currency: string }>({
      query: (data) => ({
        url: '/payments/intent',
        method: 'POST',
        data,
      }),
    }),
    
    confirmPayment: builder.mutation<void, { payment_intent_id: string; payment_method: string }>({
      query: (data) => ({
        url: '/payments/confirm',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Bookings'],
    }),
  }),
});

export const {
  useGetBookingsQuery,
  useLazyGetBookingsQuery,
  useGetBookingQuery,
  useCreateBookingMutation,
  useUpdateBookingMutation,
  useCancelBookingMutation,
  useCheckInBookingMutation,
  useGetEventTicketsQuery,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useCreatePaymentIntentMutation,
  useConfirmPaymentMutation,
} = bookingsAPI;
